// TMOS Log File Service
// Date: 2025-11-17
// Purpose: Frontend service for log file management (Platform Admin only)

import api from './api';

export interface LogFile {
  filename: string;
  size: number;
  lastModified: string;
  isRotated: boolean;
}

export interface LogFileContent {
  filename: string;
  content: string;
  totalLines: number;
  startLine: number;
  endLine: number;
  fileSize: number;
  lastModified: string;
}

export interface LogFileStats {
  filename: string;
  size: number;
  lines: number;
  firstLineDate?: string;
  lastLineDate?: string;
  lastModified: string;
}

export interface LogFileOptions {
  tail?: number;
  startLine?: number;
  endLine?: number;
  search?: string;
}

class LogFileService {
  /**
   * List available log files (Platform Admin only)
   */
  async listLogFiles(): Promise<LogFile[]> {
    const response = await api.get('/api/v1/admin/logs/files');
    return response.data.data || [];
  }

  /**
   * Read log file (Platform Admin only)
   */
  async getLogFile(filename: string, options: LogFileOptions = {}): Promise<LogFileContent> {
    const params = new URLSearchParams();
    if (options.tail) params.append('tail', options.tail.toString());
    if (options.startLine) params.append('start_line', options.startLine.toString());
    if (options.endLine) params.append('end_line', options.endLine.toString());
    if (options.search) params.append('search', options.search);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/admin/logs/file/${filename}${queryString ? `?${queryString}` : ''}`);
    return response.data.data;
  }

  /**
   * Get log file statistics (Platform Admin only)
   */
  async getLogFileStats(filename: string): Promise<LogFileStats> {
    const response = await api.get(`/api/v1/admin/logs/file/${filename}/stats`);
    return response.data.data;
  }

  /**
   * Download log file (Platform Admin only)
   */
  async downloadLogFile(filename: string): Promise<Blob> {
    const response = await api.get(`/api/v1/admin/logs/file/${filename}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Delete log file (Platform Admin only)
   */
  async deleteLogFile(filename: string): Promise<void> {
    await api.delete(`/api/v1/admin/logs/file/${filename}`);
  }

  /**
   * Cleanup old log files (Platform Admin only)
   */
  async cleanupOldLogs(days: number): Promise<{ deleted: number; errors: number }> {
    const response = await api.post('/api/v1/admin/logs/cleanup', { days });
    return response.data.data;
  }
}

export const logFileService = new LogFileService();

